<script lang="ts">
import type { DataFeed } from '@codot/contracts';
import { dataFeedsApi } from '../../api/data-feeds.api';
/** One request per feed for every preview on the page, kept a minute. */
const cache = new Map<string, { at: number; p: Promise<DataFeed> }>();
export function loadFeed(id: string): Promise<DataFeed> {
  const hit = cache.get(id);
  if (hit && Date.now() - hit.at < 60_000) return hit.p;
  const p = dataFeedsApi.get(id);
  cache.set(id, { at: Date.now(), p });
  p.catch(() => cache.delete(id));
  return p;
}
</script>

<script setup lang="ts">
/**
 * Chart app preview: the feed's approved table drawn with the same SVG as the web player (contracts chartSvg),
 * redrawn at the preview's own size.
 */
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { chartData, chartSvg, type ChartWidget } from '@codot/contracts';
import { fontFamilyCss } from '../../utils/fonts';

const props = defineProps<{ config: ChartWidget }>();
const { t } = useI18n();
const feed = shallowRef<DataFeed | null>(null);
const failed = ref(false);
watch(
  () => props.config.feedId,
  async (id) => {
    feed.value = null;
    failed.value = false;
    if (!id) return;
    try {
      feed.value = await loadFeed(id);
    } catch {
      failed.value = true;
    }
  },
  { immediate: true },
);
const box = ref<HTMLElement | null>(null);
const size = ref({ w: 0, h: 0 });
let ro: ResizeObserver | null = null;
onMounted(() => {
  ro = new ResizeObserver(() => box.value && (size.value = { w: box.value.clientWidth, h: box.value.clientHeight }));
  if (box.value) ro.observe(box.value);
});
onBeforeUnmount(() => ro?.disconnect());
const svg = computed(() => {
  const { w, h } = size.value;
  if (!w || !h || !feed.value?.table) return '';
  return chartSvg(chartData(feed.value.table, props.config), props.config, w, h, fontFamilyCss(props.config.fontId ?? null) ?? 'sans-serif');
});
const message = computed(() => (!props.config.feedId ? t('appsCat.chartPickFeed') : failed.value ? t('appsCat.chartNoFeed') : feed.value && !feed.value.table ? t('appsCat.chartNoTable') : ''));
</script>

<template>
  <div ref="box" class="h-full w-full overflow-hidden" :style="{ background: config.background, color: config.foreground }">
    <!-- eslint-disable-next-line vue/no-v-html -- built by chartSvg, every text escaped -->
    <div v-if="svg" class="h-full w-full" v-html="svg" />
    <p v-else class="flex h-full items-center justify-center px-4 text-center text-sm opacity-75">{{ message }}</p>
  </div>
</template>
